import { useCallback, useEffect, useRef, useState, type Dispatch, type SetStateAction } from 'react';
import { useUploadFile } from '@/hooks/use-upload-file';
import { useRegisterUserMedia } from '@/hooks/use-register-user-media';
import type { CaptureAndUploadOptions, CoverFrameCaptureResult } from '@/hooks/use-cover-frame-capture';
import {
  buildFrameCover,
  buildUploadCover,
  sanitizeCoverImage,
  type ClipCoverImage,
} from '@/lib/cover/coverImage';
import { aiCoverDimensions, buildAiCoverMediaName } from '@/lib/cover/aiCoverMedia';

export type CoverSourcesBusy = 'capturing' | 'uploading' | null;
export type CoverSourcesError = 'captureError' | 'uploadError' | 'invalidImage';

export interface UseCoverImageSourcesInput {
  onCommit: (cover: ClipCoverImage) => void;
  uploadFramedDataUrl: (
    dataUrl: string,
    captureTime: number,
    options?: CaptureAndUploadOptions,
  ) => Promise<CoverFrameCaptureResult>;
  exportAspect: number;
  /** Cover for an uploaded captured frame. Default: buildFrameCover(url, captureTime). */
  buildCapturedCover?: (url: string, captureTime: number) => ClipCoverImage;
}

/**
 * The cover source actions shared by the editor's "Capa" panel and the /clips
 * cover dialog: upload a computer image, upload a captured frame, accept an
 * AI cover, each sanitized and (except AI reference frames) registered in My
 * Media. The caller decides what "commit" means. Results that arrive after
 * unmount are dropped.
 */
export function useCoverImageSources({
  onCommit,
  uploadFramedDataUrl,
  exportAspect,
  buildCapturedCover = buildFrameCover,
}: UseCoverImageSourcesInput) {
  const { getUploadUrl } = useUploadFile();
  const registerUserMedia = useRegisterUserMedia();
  const mountedRef = useRef(true);
  const [busy, setBusy] = useState<CoverSourcesBusy>(null);
  const [error, setError] = useState<CoverSourcesError | null>(null);

  useEffect(() => {
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;
    };
  }, []);

  const isActive = useCallback(() => mountedRef.current, []);

  // Every picked/uploaded/captured cover goes through the same sanitizer the
  // saved data does, so a URL that would be silently dropped on save (e.g. a
  // non-https upload) is rejected here with an inline error instead.
  const commitCandidate = (candidate: ClipCoverImage): boolean => {
    const next = sanitizeCoverImage(candidate);
    if (!next) {
      setError('invalidImage');
      return false;
    }
    onCommit(next);
    return true;
  };

  const commitCapturedFrame = async (dataUrl: string, captureTime: number): Promise<void> => {
    setError(null);
    setBusy('uploading');
    try {
      const result = await uploadFramedDataUrl(dataUrl, captureTime, { isActive });
      if (!mountedRef.current) return;
      if (result.ok === false) {
        if (result.error !== 'cancelled') setError(result.error);
        return;
      }
      // Rejected before registering: an unusable URL must not add a My Media row.
      const next = sanitizeCoverImage(buildCapturedCover(result.url, result.captureTime));
      if (!next) {
        setError('invalidImage');
        return;
      }
      registerUserMedia({ url: result.url, kind: 'image', name: result.fileName });
      onCommit(next);
    } finally {
      if (mountedRef.current) setBusy(null);
    }
  };

  // The AI reference frame: same upload path, never registered in My Media
  // and never committed as a cover.
  const uploadReference = useCallback(
    async (dataUrl: string, captureTime: number): Promise<string | null> => {
      const result = await uploadFramedDataUrl(dataUrl, captureTime, { isActive });
      return result.ok ? result.url : null;
    },
    [uploadFramedDataUrl, isActive],
  );

  const uploadFile = async (file: File): Promise<void> => {
    setError(null);
    setBusy('uploading');
    try {
      const url = await getUploadUrl(file);
      if (!mountedRef.current) return;
      if (!url) {
        setError('uploadError');
        return;
      }
      const next = sanitizeCoverImage(buildUploadCover(url));
      if (!next) {
        setError('invalidImage');
        return;
      }
      registerUserMedia({ url, kind: 'image', name: file.name });
      onCommit(next);
    } catch {
      if (mountedRef.current) setError('uploadError');
    } finally {
      if (mountedRef.current) setBusy(null);
    }
  };

  const commitAiCover = (url: string): void => {
    setError(null);
    if (!commitCandidate(buildUploadCover(url))) return;
    // Backstop for the backend listener's registration (deduped by url): a
    // missed listener must not leave the cover unprotected from the storage
    // reaper. The AI reference frame is never registered.
    registerUserMedia({
      url,
      kind: 'image',
      name: buildAiCoverMediaName(new Date()),
      ...aiCoverDimensions(exportAspect),
    });
  };

  return {
    busy,
    setBusy: setBusy as Dispatch<SetStateAction<CoverSourcesBusy>>,
    error,
    setError,
    isActive,
    commitCandidate,
    uploadFile,
    commitCapturedFrame,
    uploadReference,
    commitAiCover,
  };
}
